import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";

/**
 * Order Sync -> Duplicates.
 *
 * Self-service repair for shipments that exist twice in the driver (TMS) database:
 *  - check one AWB, or scan a date range
 *  - see every copy side by side (status, driver, scans, COD) so it is obvious which one is real
 *  - keep one copy and cancel the rest, with a Preview that writes nothing
 *
 * New duplicates are stopped in the backend at create time; this screen clears the backlog and
 * handles the cases the guard deliberately only flags (same customer AWB booked twice).
 */
export default function OrderDuplicates({ initialAwb = "" }) {
  const API_URL = process.env.REACT_APP_API_URL;
  const baseUrl = `${API_URL}shypvdriverapp/api/sync/duplicates`;

  const today = new Date().toISOString().slice(0, 10);
  const weekAgo = new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const [mode, setMode] = useState("awb"); // "awb" | "range"
  const [awb, setAwb] = useState(initialAwb || "");
  const [fromDate, setFromDate] = useState(weekAgo);
  const [toDate, setToDate] = useState(today);
  const [partnerId, setPartnerId] = useState("");

  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [banner, setBanner] = useState(null); // { type, text }

  // Per-group operator choices: { [groupKey]: { keeperId, removeIds: [], busy, result } }
  const [choices, setChoices] = useState({});
  const [removeMode, setRemoveMode] = useState("CANCEL"); // "CANCEL" | "DELETE"

  const [guard, setGuard] = useState(null);
  const [showGuardLog, setShowGuardLog] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const resolvedBy = (typeof window !== "undefined" && localStorage.getItem("userid")) || "";
  const resolvedByName =
    (typeof window !== "undefined" && (localStorage.getItem("user_name") || localStorage.getItem("name"))) || "";

  // ------------------------------------------------------------------ styles
  const styles = {
    container: { padding: "4px 0 24px 0" },
    card: {
      backgroundColor: "#ffffff",
      borderRadius: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      marginBottom: "16px",
      overflow: "hidden",
    },
    cardHeader: {
      padding: "14px 20px",
      borderBottom: "1px solid #edf2f7",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "12px",
      flexWrap: "wrap",
    },
    cardTitle: { fontSize: "15px", fontWeight: 600, color: "#2d3748", margin: 0 },
    cardBody: { padding: "18px 20px" },
    row: { display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "flex-end" },
    group: { display: "flex", flexDirection: "column", gap: "6px" },
    label: { fontSize: "12px", fontWeight: 600, color: "#4a5568" },
    input: {
      padding: "9px 12px",
      border: "1px solid #e2e8f0",
      borderRadius: "8px",
      fontSize: "13px",
      minWidth: "170px",
      outline: "none",
    },
    select: {
      padding: "9px 12px",
      border: "1px solid #e2e8f0",
      borderRadius: "8px",
      fontSize: "13px",
      background: "#fff",
      outline: "none",
    },
    btn: {
      padding: "9px 16px",
      borderRadius: "8px",
      border: "none",
      fontSize: "13px",
      fontWeight: 600,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: "7px",
    },
    btnPrimary: { backgroundColor: "#4f7df3", color: "#fff" },
    btnGhost: { backgroundColor: "#edf2f7", color: "#2d3748" },
    btnWarn: { backgroundColor: "#f59e0b", color: "#fff" },
    btnDanger: { backgroundColor: "#e53e3e", color: "#fff" },
    alert: {
      padding: "12px 16px",
      borderRadius: "8px",
      display: "flex",
      alignItems: "flex-start",
      gap: "10px",
      fontSize: "13px",
      marginBottom: "14px",
      lineHeight: 1.5,
    },
    alertInfo: { backgroundColor: "#ebf4ff", color: "#2c5282", border: "1px solid #bee3f8" },
    alertWarn: { backgroundColor: "#fffaf0", color: "#975a16", border: "1px solid #fbd38d" },
    alertError: { backgroundColor: "#fff5f5", color: "#c53030", border: "1px solid #feb2b2" },
    alertOk: { backgroundColor: "#f0fff4", color: "#276749", border: "1px solid #9ae6b4" },
    badge: {
      display: "inline-block",
      padding: "3px 9px",
      borderRadius: "999px",
      fontSize: "11px",
      fontWeight: 700,
      letterSpacing: "0.3px",
    },
    table: { width: "100%", borderCollapse: "collapse", fontSize: "12.5px" },
    th: {
      textAlign: "left",
      padding: "9px 10px",
      background: "#f7fafc",
      color: "#4a5568",
      fontWeight: 700,
      borderBottom: "1px solid #e2e8f0",
      whiteSpace: "nowrap",
    },
    td: { padding: "9px 10px", borderBottom: "1px solid #edf2f7", verticalAlign: "top" },
    stat: {
      background: "#f7fafc",
      borderRadius: "10px",
      padding: "12px 16px",
      minWidth: "130px",
      border: "1px solid #edf2f7",
    },
    statValue: { fontSize: "20px", fontWeight: 700, color: "#2d3748" },
    statLabel: { fontSize: "11px", color: "#718096", fontWeight: 600, textTransform: "uppercase" },
    spinner: {
      width: "13px",
      height: "13px",
      border: "2px solid rgba(255,255,255,0.4)",
      borderTopColor: "#fff",
      borderRadius: "50%",
      display: "inline-block",
      animation: "spin 0.7s linear infinite",
    },
  };

  const kindBadge = (kind) =>
    kind === "EXACT_REPLAY"
      ? { ...styles.badge, background: "#fed7d7", color: "#9b2c2c" }
      : { ...styles.badge, background: "#feebc8", color: "#975a16" };

  const statusBadge = (status) => {
    const s = (status || "").toUpperCase();
    if (s === "DELIVERED") return { ...styles.badge, background: "#c6f6d5", color: "#22543d" };
    if (s === "CANCELLED") return { ...styles.badge, background: "#e2e8f0", color: "#4a5568" };
    if (s === "UNASSIGNED") return { ...styles.badge, background: "#e9d8fd", color: "#553c9a" };
    return { ...styles.badge, background: "#bee3f8", color: "#2a4365" };
  };

  // ------------------------------------------------------------------ data
  const applyScan = (data) => {
    setScan(data);
    const next = {};
    (data?.groups || []).forEach((g) => {
      next[g.groupKey] = {
        keeperId: g.recommendedKeeperId,
        removeIds: g.recommendedRemoveIds || [],
        busy: false,
        result: null,
      };
    });
    setChoices(next);
  };

  const errorText = (err, fallback) =>
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback;

  const loadGuard = useCallback(async () => {
    try {
      const res = await axios.get(`${baseUrl}/guard?limit=50`);
      setGuard(res.data?.data || null);
    } catch (err) {
      // Guard status is informational - never block the screen on it.
      setGuard(null);
    }
  }, [baseUrl]);

  useEffect(() => {
    loadGuard();
  }, [loadGuard]);

  const checkAwb = async () => {
    if (!awb.trim()) {
      setBanner({ type: "warn", text: "Enter an AWB / booking id first." });
      return;
    }
    setLoading(true);
    setError("");
    setBanner(null);
    try {
      const res = await axios.get(`${baseUrl}/check/${encodeURIComponent(awb.trim())}`);
      applyScan(res.data?.data || null);
    } catch (err) {
      setError(errorText(err, "Could not check this AWB."));
      setScan(null);
    } finally {
      setLoading(false);
    }
  };

  const runScan = async () => {
    setLoading(true);
    setError("");
    setBanner(null);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append("fromDate", fromDate);
      if (toDate) params.append("toDate", toDate);
      if (partnerId.trim()) params.append("partnerId", partnerId.trim());
      const res = await axios.get(`${baseUrl}/scan?${params.toString()}`);
      applyScan(res.data?.data || null);
    } catch (err) {
      setError(errorText(err, "Scan failed."));
      setScan(null);
    } finally {
      setLoading(false);
    }
  };

  const resolveGroup = async (group, dryRun) => {
    const choice = choices[group.groupKey];
    if (!choice?.keeperId) {
      setBanner({ type: "warn", text: "Choose which copy to keep first." });
      return;
    }
    if (!choice.removeIds || choice.removeIds.length === 0) {
      setBanner({ type: "warn", text: "Tick at least one copy to remove." });
      return;
    }
    if (!dryRun) {
      const verb = removeMode === "DELETE" ? "permanently delete" : "cancel";
      const ok = window.confirm(
        `Keep order #${choice.keeperId} and ${verb} ${choice.removeIds.length} other copy/copies?\n\n` +
          `${removeMode === "DELETE" ? "Deleted rows cannot be recovered." : "Cancelled rows stay in the database and can be reopened."}`
      );
      if (!ok) return;
    }

    setChoices((prev) => ({
      ...prev,
      [group.groupKey]: { ...prev[group.groupKey], busy: true },
    }));
    try {
      const res = await axios.post(`${baseUrl}/resolve`, {
        keepOrderId: choice.keeperId,
        removeOrderIds: choice.removeIds,
        mode: removeMode,
        resolvedBy,
        reason: `Duplicate resolved from Order Sync by ${resolvedByName || "admin"}`,
        dryRun,
      });
      const result = res.data?.data || null;
      setChoices((prev) => ({
        ...prev,
        [group.groupKey]: { ...prev[group.groupKey], busy: false, result },
      }));
      setBanner({
        type: result?.success ? "ok" : "warn",
        text: result?.message || (dryRun ? "Preview ready." : "Done."),
      });
      if (!dryRun && result?.success) {
        // Re-run the same search so the screen reflects the database.
        if (mode === "awb") checkAwb();
        else runScan();
        loadGuard();
      }
    } catch (err) {
      setChoices((prev) => ({
        ...prev,
        [group.groupKey]: { ...prev[group.groupKey], busy: false },
      }));
      setError(errorText(err, "Could not resolve this group."));
    }
  };

  const autoResolve = async (dryRun) => {
    if (!dryRun) {
      const ok = window.confirm(
        "Auto-fix every exact duplicate in this date range?\n\n" +
          "Only repeated task ids are touched. Same-AWB re-bookings and groups where two copies " +
          "were both worked by a driver are always left for you to decide."
      );
      if (!ok) return;
    }
    setLoading(true);
    setError("");
    setBanner(null);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append("fromDate", fromDate);
      if (toDate) params.append("toDate", toDate);
      if (partnerId.trim()) params.append("partnerId", partnerId.trim());
      params.append("dryRun", String(dryRun));
      if (resolvedBy) params.append("resolvedBy", resolvedBy);
      const res = await axios.post(`${baseUrl}/auto-resolve?${params.toString()}`);
      const data = res.data?.data;
      setBanner({
        type: "ok",
        text:
          (dryRun ? "Preview - nothing was written. " : "") +
          `${data?.groupsResolved ?? 0} of ${data?.groupsProcessed ?? 0} group(s) ` +
          `${dryRun ? "would be" : "were"} resolved.`,
      });
      if (!dryRun) {
        await runScan();
        loadGuard();
      }
    } catch (err) {
      setError(errorText(err, "Auto-fix failed."));
    } finally {
      setLoading(false);
    }
  };

  const setKeeper = (group, orderId) => {
    setChoices((prev) => {
      const current = prev[group.groupKey] || {};
      const others = (group.members || [])
        .map((m) => m.orderId)
        .filter((id) => id !== orderId);
      // Keep whatever was already ticked, minus the new keeper; default to all others.
      const previouslyTicked = current.removeIds || [];
      const removeIds = previouslyTicked.length
        ? previouslyTicked.filter((id) => id !== orderId)
        : others;
      return {
        ...prev,
        [group.groupKey]: { ...current, keeperId: orderId, removeIds, result: null },
      };
    });
  };

  const toggleRemove = (group, orderId) => {
    setChoices((prev) => {
      const current = prev[group.groupKey] || { removeIds: [] };
      const set = new Set(current.removeIds || []);
      if (set.has(orderId)) set.delete(orderId);
      else set.add(orderId);
      return {
        ...prev,
        [group.groupKey]: { ...current, removeIds: Array.from(set), result: null },
      };
    });
  };

  // ------------------------------------------------------------------ render
  const groups = scan?.groups || [];

  return (
    <div style={styles.container}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* What this screen is for */}
      <div style={{ ...styles.alert, ...styles.alertInfo }}>
        <i className="fa fa-clone" style={{ marginTop: "2px" }} />
        <div>
          <strong>Duplicates:</strong> one shipment written into the driver database more than once.
          Find them, keep the copy the driver actually worked, and cancel the rest - no DB access needed.
          <button
            style={{ ...styles.btn, ...styles.btnGhost, marginLeft: "10px", padding: "4px 10px" }}
            onClick={() => setShowHelp((v) => !v)}
          >
            <i className={`fa fa-chevron-${showHelp ? "up" : "down"}`} />
            {showHelp ? "Hide" : "How do I fix one?"}
          </button>
          {showHelp && (
            <ol style={{ margin: "10px 0 0 0", paddingLeft: "18px", lineHeight: 1.7 }}>
              <li>Search by the AWB the partner/customer complained about, or scan yesterday's date range.</li>
              <li>
                Look at the copies. <strong>Repeated task id</strong> (red) is always wrong.{" "}
                <strong>Same customer AWB</strong> (orange) means the shipment was booked twice - check
                with the partner if unsure.
              </li>
              <li>
                The row marked <em>Recommended keep</em> is the one with the most progress (driver
                assigned, scanned, delivered). Change it if you know better.
              </li>
              <li>
                Hit <strong>Preview</strong> to see exactly what would change, then <strong>Apply</strong>.
                Driver, COD, warehouse and scan data from the other copies is merged into the copy you keep.
              </li>
              <li>
                Cancelled copies stay in the database (status CANCELLED) and can be reopened - nothing is
                lost. Only untouched rows can be hard-deleted.
              </li>
            </ol>
          )}
        </div>
      </div>

      {/* Guard status */}
      {guard && (
        <div
          style={{
            ...styles.alert,
            ...(guard.mode === "OFF" ? styles.alertWarn : styles.alertOk),
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
            <i className="fa fa-shield" style={{ marginTop: "2px" }} />
            <div>
              <strong>Create-time guard: {guard.mode}</strong> - a repeated task id is never inserted
              twice{guard.mode === "BLOCK" ? ", and a re-booked customer AWB is refused" : ""}
              {guard.mode === "FLAG" ? ", and a re-booked customer AWB is created but listed here" : ""}
              {guard.mode === "OFF" ? ". Same-AWB checking is switched off." : "."}{" "}
              {(guard.recent || []).length > 0 && (
                <button
                  style={{ ...styles.btn, ...styles.btnGhost, padding: "3px 9px", marginLeft: "4px" }}
                  onClick={() => setShowGuardLog((v) => !v)}
                >
                  {(guard.recent || []).length} caught since restart
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showGuardLog && guard?.recent?.length > 0 && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Duplicate creates caught by the guard</h3>
            <button style={{ ...styles.btn, ...styles.btnGhost }} onClick={loadGuard}>
              <i className="fa fa-refresh" /> Refresh
            </button>
          </div>
          <div style={{ ...styles.cardBody, maxHeight: "260px", overflowY: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>When</th>
                  <th style={styles.th}>Task id</th>
                  <th style={styles.th}>Leg</th>
                  <th style={styles.th}>Customer AWB</th>
                  <th style={styles.th}>What happened</th>
                </tr>
              </thead>
              <tbody>
                {guard.recent.map((r, i) => (
                  <tr key={i}>
                    <td style={styles.td}>{(r.at || "").replace("T", " ").slice(0, 19)}</td>
                    <td style={styles.td}>{r.jobId}</td>
                    <td style={styles.td}>{r.type}</td>
                    <td style={styles.td}>{r.businessRefNo || "-"}</td>
                    <td style={styles.td}>{r.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Search */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>
            <i className="fa fa-search" style={{ marginRight: "8px" }} />
            Find duplicates
          </h3>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              style={{ ...styles.btn, ...(mode === "awb" ? styles.btnPrimary : styles.btnGhost) }}
              onClick={() => setMode("awb")}
            >
              Single AWB
            </button>
            <button
              style={{ ...styles.btn, ...(mode === "range" ? styles.btnPrimary : styles.btnGhost) }}
              onClick={() => setMode("range")}
            >
              Date range
            </button>
          </div>
        </div>
        <div style={styles.cardBody}>
          {mode === "awb" ? (
            <div style={styles.row}>
              <div style={{ ...styles.group, flex: 1 }}>
                <label style={styles.label}>AWB / Booking id / Customer reference</label>
                <input
                  style={{ ...styles.input, width: "100%" }}
                  value={awb}
                  onChange={(e) => setAwb(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && checkAwb()}
                  placeholder="e.g. UB32-26C0454, UB32-26C0454_1 or the partner's own AWB"
                />
              </div>
              <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={checkAwb} disabled={loading}>
                {loading ? <span style={styles.spinner} /> : <i className="fa fa-search" />}
                Check
              </button>
            </div>
          ) : (
            <>
              <div style={styles.row}>
                <div style={styles.group}>
                  <label style={styles.label}>From</label>
                  <input
                    type="date"
                    style={styles.input}
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>
                <div style={styles.group}>
                  <label style={styles.label}>To</label>
                  <input
                    type="date"
                    style={styles.input}
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
                <div style={styles.group}>
                  <label style={styles.label}>Partner id (optional)</label>
                  <input
                    style={styles.input}
                    value={partnerId}
                    onChange={(e) => setPartnerId(e.target.value)}
                    placeholder="all partners"
                  />
                </div>
                <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={runScan} disabled={loading}>
                  {loading ? <span style={styles.spinner} /> : <i className="fa fa-search" />}
                  Scan
                </button>
                <button
                  style={{ ...styles.btn, ...styles.btnGhost }}
                  onClick={() => autoResolve(true)}
                  disabled={loading}
                >
                  <i className="fa fa-eye" /> Preview auto-fix
                </button>
                <button
                  style={{ ...styles.btn, ...styles.btnWarn }}
                  onClick={() => autoResolve(false)}
                  disabled={loading}
                >
                  <i className="fa fa-magic" /> Apply auto-fix
                </button>
              </div>
              <div style={{ fontSize: "12px", color: "#718096", marginTop: "10px" }}>
                Auto-fix only clears repeated task ids, and never a group where two copies were both
                worked by a driver. Everything else stays here for you to decide.
              </div>
            </>
          )}
        </div>
      </div>

      {/* Banner / error */}
      {error && (
        <div style={{ ...styles.alert, ...styles.alertError }}>
          <i className="fa fa-exclamation-circle" />
          <div style={{ flex: 1 }}>{error}</div>
          <button
            style={{ background: "none", border: "none", cursor: "pointer", color: "#c53030" }}
            onClick={() => setError("")}
          >
            <i className="fa fa-times" />
          </button>
        </div>
      )}
      {banner && (
        <div
          style={{
            ...styles.alert,
            ...(banner.type === "ok" ? styles.alertOk : banner.type === "warn" ? styles.alertWarn : styles.alertInfo),
          }}
        >
          <i className="fa fa-info-circle" />
          <div style={{ flex: 1 }}>{banner.text}</div>
          <button
            style={{ background: "none", border: "none", cursor: "pointer", color: "inherit" }}
            onClick={() => setBanner(null)}
          >
            <i className="fa fa-times" />
          </button>
        </div>
      )}

      {/* Summary */}
      {scan && (
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "16px" }}>
          <div style={styles.stat}>
            <div style={styles.statValue}>{scan.exactGroups ?? 0}</div>
            <div style={styles.statLabel}>Repeated task ids</div>
          </div>
          <div style={styles.stat}>
            <div style={styles.statValue}>{scan.sameShipmentGroups ?? 0}</div>
            <div style={styles.statLabel}>Same AWB, 2 bookings</div>
          </div>
          <div style={styles.stat}>
            <div style={styles.statValue}>{scan.totalDuplicateRows ?? 0}</div>
            <div style={styles.statLabel}>Extra rows to clear</div>
          </div>
          <div style={{ ...styles.stat, display: "flex", alignItems: "center" }}>
            <div>
              <div style={styles.statLabel}>Remove by</div>
              <select
                style={{ ...styles.select, marginTop: "4px" }}
                value={removeMode}
                onChange={(e) => setRemoveMode(e.target.value)}
              >
                <option value="CANCEL">Cancel (reversible)</option>
                <option value="DELETE">Delete (untouched rows only)</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {scan && groups.length === 0 && (
        <div style={{ ...styles.alert, ...styles.alertOk }}>
          <i className="fa fa-check-circle" />
          <div>{scan.message || "No duplicates found."}</div>
        </div>
      )}

      {scan?.truncated && (
        <div style={{ ...styles.alert, ...styles.alertWarn }}>
          <i className="fa fa-exclamation-triangle" />
          <div>Too many groups to show at once - narrow the date range to see the rest.</div>
        </div>
      )}

      {/* Groups */}
      {groups.map((group) => {
        const choice = choices[group.groupKey] || {};
        const removeIds = choice.removeIds || [];
        return (
          <div key={group.groupKey} style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <span style={kindBadge(group.kind)}>
                  {group.kind === "EXACT_REPLAY" ? "REPEATED TASK ID" : "SAME AWB, 2 BOOKINGS"}
                </span>
                <h3 style={styles.cardTitle}>{group.groupKey}</h3>
                <span style={{ ...styles.badge, background: "#edf2f7", color: "#4a5568" }}>
                  {group.rowCount} copies
                </span>
                {group.needsManualReview && (
                  <span style={{ ...styles.badge, background: "#fed7d7", color: "#9b2c2c" }}>
                    NEEDS A HUMAN
                  </span>
                )}
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  style={{ ...styles.btn, ...styles.btnGhost }}
                  onClick={() => resolveGroup(group, true)}
                  disabled={choice.busy}
                >
                  {choice.busy ? <span style={{ ...styles.spinner, borderTopColor: "#2d3748" }} /> : <i className="fa fa-eye" />}
                  Preview
                </button>
                <button
                  style={{ ...styles.btn, ...(removeMode === "DELETE" ? styles.btnDanger : styles.btnPrimary) }}
                  onClick={() => resolveGroup(group, false)}
                  disabled={choice.busy}
                >
                  {choice.busy ? <span style={styles.spinner} /> : <i className="fa fa-check" />}
                  Apply
                </button>
              </div>
            </div>

            <div style={styles.cardBody}>
              <div style={{ fontSize: "13px", color: "#4a5568", marginBottom: "12px" }}>
                {group.explanation}
              </div>

              <div style={{ overflowX: "auto" }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Keep</th>
                      <th style={styles.th}>Remove</th>
                      <th style={styles.th}>Order #</th>
                      <th style={styles.th}>Task id</th>
                      <th style={styles.th}>Leg</th>
                      <th style={styles.th}>Status</th>
                      <th style={styles.th}>Driver</th>
                      <th style={styles.th}>Scanned</th>
                      <th style={styles.th}>COD</th>
                      <th style={styles.th}>Customer</th>
                      <th style={styles.th}>Created</th>
                      <th style={styles.th}>History</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(group.members || []).map((m) => {
                      const isKeeper = choice.keeperId === m.orderId;
                      return (
                        <tr key={m.orderId} style={isKeeper ? { background: "#f0fff4" } : undefined}>
                          <td style={styles.td}>
                            <input
                              type="radio"
                              name={`keeper-${group.groupKey}`}
                              checked={isKeeper}
                              onChange={() => setKeeper(group, m.orderId)}
                            />
                          </td>
                          <td style={styles.td}>
                            <input
                              type="checkbox"
                              disabled={isKeeper}
                              checked={removeIds.includes(m.orderId)}
                              onChange={() => toggleRemove(group, m.orderId)}
                            />
                          </td>
                          <td style={styles.td}>
                            #{m.orderId}
                            {m.recommendedKeeper && (
                              <div style={{ ...styles.badge, background: "#c6f6d5", color: "#22543d", marginTop: "4px" }}>
                                RECOMMENDED KEEP
                              </div>
                            )}
                            {m.hasDriverProgress && !m.recommendedKeeper && (
                              <div style={{ ...styles.badge, background: "#feebc8", color: "#975a16", marginTop: "4px" }}>
                                DRIVER WORKED THIS
                              </div>
                            )}
                          </td>
                          <td style={styles.td}>{m.jobId}</td>
                          <td style={styles.td}>{m.type}</td>
                          <td style={styles.td}>
                            <span style={statusBadge(m.status)}>{m.status || "-"}</span>
                          </td>
                          <td style={styles.td}>{m.driverName || m.driverId || "-"}</td>
                          <td style={styles.td}>{m.scanDateTime ? m.scanDateTime.replace("T", " ").slice(0, 16) : "-"}</td>
                          <td style={styles.td}>{m.codAmount ? m.codAmount.toFixed(2) : "-"}</td>
                          <td style={styles.td}>
                            {m.customerName || "-"}
                            {m.address ? (
                              <div style={{ color: "#718096", fontSize: "11.5px", maxWidth: "260px" }}>{m.address}</div>
                            ) : null}
                          </td>
                          <td style={styles.td}>
                            {m.createdDate ? m.createdDate.replace("T", " ").slice(0, 16) : "-"}
                          </td>
                          <td style={styles.td}>{m.historyCount ?? 0}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {choice.result && (
                <div
                  style={{
                    ...styles.alert,
                    ...(choice.result.success ? styles.alertOk : styles.alertWarn),
                    marginTop: "14px",
                    marginBottom: 0,
                    flexDirection: "column",
                    alignItems: "stretch",
                  }}
                >
                  <div style={{ fontWeight: 600 }}>
                    {choice.result.dryRun ? "Preview - nothing written. " : ""}
                    {choice.result.message}
                  </div>
                  {choice.result.mergedFields?.length > 0 && (
                    <div style={{ fontSize: "12px" }}>
                      Merged into the copy kept: {choice.result.mergedFields.join(", ")}
                    </div>
                  )}
                  <ul style={{ margin: "8px 0 0 0", paddingLeft: "18px", fontSize: "12px" }}>
                    {(choice.result.outcomes || []).map((o, i) => (
                      <li key={i}>
                        <strong>{o.action}</strong> #{o.orderId} {o.jobId ? `(${o.jobId})` : ""} - {o.detail}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
